import { Fragment, useCallback, useMemo, useState } from 'react'

import {
  Alert,
  AlertTitle,
  Box,
  ClickAwayListener,
  Divider,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import type { Draft } from 'types/graphql'

import ObjectChip from 'src/components/Chips/ObjectChip/ObjectChip'
import ObjectChipRow from 'src/components/Chips/ObjectChipRow/ObjectChipRow'
import ContactSelect from 'src/components/ContactSelect/ContactSelect'
import Row from 'src/components/Row/Row'
import { NativeObjectTypes } from 'src/lib/objects'

import DraftHeaderRow from '../../DraftHeaderRow'
import { useValidatedFromAddress } from '../../utils'

const RecipientsList = ({
  draft,
  onClick,
}: {
  draft: Draft
  onClick: () => void
}) => {
  const recipients = [...draft.email.to, ...draft.email.cc, ...draft.email.bcc]
  return (
    <DraftHeaderRow
      label={recipients.length === 0 ? 'Recipients' : ''}
      onClick={onClick}
      disabled={recipients.length === 0}
    >
      <ObjectChipRow
        showSidebar={true}
        objects={recipients.map((recipient) => ({
          objectId: recipient,
          objectType: NativeObjectTypes.Contact,
        }))}
        workspaceId={draft.workspaceId}
      />
    </DraftHeaderRow>
  )
}

const draftRecipientsSx = {
  flexWrap: 'wrap',
  width: 'auto',
  '& .MuiChip-root': {
    maxWidth: '188px',
  },
}

const contactSelectSx = { width: '200px' }

const subjectFieldSx = {
  width: '100%',
  '& .MuiInputBase-input::placeholder': {
    color: (theme) => theme.palette.text.disabled,
    opacity: 1,
  },
}

const fromSelectSx = {
  width: '200px',
  border: 'none',
  boxShadow: 'none',
  '.MuiOutlinedInput-notchedOutline': { border: 0 },
  '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    border: 0,
  },
  '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: 0,
  },
  '& .MuiSelect-select': {
    padding: 0,
  },
}

const EditRecipientsField = ({
  draft,
  targetField,
  handleAddEmailRecipient,
  handleRemoveEmailRecipient,
}: {
  draft: Draft
  targetField: 'to' | 'cc' | 'bcc'
  handleAddEmailRecipient: (
    recipient: string,
    field: 'to' | 'cc' | 'bcc'
  ) => void
  handleRemoveEmailRecipient: (
    recipient: string,
    field: 'to' | 'cc' | 'bcc'
  ) => void
}) => {
  const recipients = useMemo(() => {
    return draft.email?.[targetField] || []
  }, [draft.email, targetField])

  const handleBackspace = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (
        e.key === 'Backspace' &&
        (e.target as HTMLInputElement).value === ''
      ) {
        handleRemoveEmailRecipient(
          recipients[recipients.length - 1],
          targetField
        )
      }
    },
    [handleRemoveEmailRecipient, recipients, targetField]
  )
  return (
    <DraftHeaderRow
      label={targetField === 'to' ? 'To' : targetField === 'cc' ? 'Cc' : 'Bcc'}
    >
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {recipients.map((recipient) => (
          <Fragment key={`${recipient}-${draft.id}-${targetField}`}>
            <Tooltip title={recipient}>
              <Box
                sx={draftRecipientsSx}
                gap={1}
              >
                <ObjectChip
                  fullWidth
                  showSidebar={true}
                  crmObject={{
                    objectId: recipient,
                    objectType: NativeObjectTypes.Contact,
                  }}
                  workspaceId={draft.workspaceId}
                  onRemove={() =>
                    handleRemoveEmailRecipient(recipient, targetField)
                  }
                />
              </Box>
            </Tooltip>
          </Fragment>
        ))}
        <Box>
          <ContactSelect
            sx={contactSelectSx}
            exclude={recipients}
            editing={true}
            onKeyDown={handleBackspace}
            onSelect={(contacts) => {
              handleAddEmailRecipient(
                contacts.map((contact) => contact.objectId)[0],
                targetField
              )
            }}
          />
        </Box>
      </Box>
    </DraftHeaderRow>
  )
}

const SubjectField = ({
  draft,
  handleUpdateEmailSubject,
}: {
  draft: Draft
  handleUpdateEmailSubject: (subject: string) => void
}) => {
  return (
    <DraftHeaderRow label="Subject:">
      <TextField
        value={draft.email.subject}
        placeholder="Subject"
        variant="standard"
        sx={subjectFieldSx}
        InputProps={{
          disableUnderline: true,
        }}
        onChange={(e) => {
          handleUpdateEmailSubject(e.target.value)
        }}
      />
    </DraftHeaderRow>
  )
}

const FromField = ({
  draft,
  handleUpdateEmailFrom,
}: {
  draft: Draft
  handleUpdateEmailFrom: (from: string) => void
}) => {
  const { validatedFromAddress, workAccountsWithGmailStatus } =
    useValidatedFromAddress(draft)

  return (
    <DraftHeaderRow label="From">
      {validatedFromAddress ? (
        workAccountsWithGmailStatus.length > 1 ? (
          <FormControl
            variant="outlined"
            size="small"
          >
            <Select
              sx={fromSelectSx}
              value={draft.email.from}
              onChange={(e) => {
                handleUpdateEmailFrom(e.target.value)
              }}
            >
              {workAccountsWithGmailStatus.map((workAccount) => (
                <MenuItem
                  key={workAccount.email}
                  value={workAccount.email}
                >
                  {workAccount.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Typography variant="body2">{draft.email.from}</Typography>
        )
      ) : (
        <Alert
          severity="warning"
          sx={{ width: '100%' }}
        >
          Please connect a work account to send emails
        </Alert>
      )}
    </DraftHeaderRow>
  )
}

const ComposeEmailDraftHeader = ({
  draft,
  handleUpdateEmailFrom,
  handleUpdateEmailSubject,
  handleAddEmailRecipient,
  handleRemoveEmailRecipient,
  errorSaving,
}: {
  draft: Draft
  handleUpdateEmailFrom: (from: string) => void
  handleUpdateEmailSubject: (subject: string) => void
  handleAddEmailRecipient: (
    recipient: string,
    field: 'to' | 'cc' | 'bcc'
  ) => void
  handleRemoveEmailRecipient: (
    recipient: string,
    field: 'to' | 'cc' | 'bcc'
  ) => void
  errorSaving: boolean
}) => {
  const [headerVisibility, setHeaderVisibility] = useState<
    Record<string, boolean>
  >({
    to: true,
    cc: false,
    bcc: false,
  })

  const [editingHeaders, setEditingHeaders] = useState<boolean>(false)

  const handleClickAway = useCallback(() => {
    setHeaderVisibility({
      to: true,
      cc: draft.email.cc.length > 0,
      bcc: draft.email.bcc.length > 0,
    })
    setEditingHeaders(false)
  }, [draft.email])

  return (
    <Box sx={{ justifyContent: 'space-between', mx: 1 }}>
      <ClickAwayListener
        mouseEvent="onMouseDown"
        onClickAway={handleClickAway}
      >
        <Box sx={{ mt: 1 }}>
          {editingHeaders && (
            <Fragment>
              <FromField
                draft={draft}
                handleUpdateEmailFrom={handleUpdateEmailFrom}
              />
              <Divider sx={{ my: 1 }} />
            </Fragment>
          )}

          <Row
            sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}
          >
            {!editingHeaders && (
              <RecipientsList
                draft={draft}
                onClick={() => setEditingHeaders(true)}
              />
            )}

            <Box>
              {Object.keys(headerVisibility).map((field) => (
                <Fragment key={field}>
                  {editingHeaders && headerVisibility[field] && (
                    <EditRecipientsField
                      draft={draft}
                      targetField={field as 'to' | 'cc' | 'bcc'}
                      handleAddEmailRecipient={handleAddEmailRecipient}
                      handleRemoveEmailRecipient={handleRemoveEmailRecipient}
                    />
                  )}
                </Fragment>
              ))}
            </Box>
            <Box gap={1}>
              {editingHeaders &&
                Object.keys(headerVisibility)
                  .filter((field) => !headerVisibility[field])
                  .map((field) => (
                    <Typography
                      key={field}
                      variant="caption"
                      sx={{ pl: 1, cursor: 'pointer' }}
                      color="text.secondary"
                      onClick={() =>
                        setHeaderVisibility((prev) => ({
                          ...prev,
                          [field]: true,
                        }))
                      }
                    >
                      {field === 'to' ? 'To' : field === 'cc' ? 'Cc' : 'Bcc'}
                    </Typography>
                  ))}
            </Box>
          </Row>
        </Box>
      </ClickAwayListener>
      <Divider sx={{ my: 1 }} />
      <SubjectField
        draft={draft}
        handleUpdateEmailSubject={handleUpdateEmailSubject}
      />
      <Divider sx={{ my: 1 }} />
      {errorSaving && (
        <Alert color="error">
          <AlertTitle>Error saving draft</AlertTitle>
          Your changes were not saved. Please make a copy of any changes and
          refresh the page.
        </Alert>
      )}
    </Box>
  )
}

export default ComposeEmailDraftHeader
